import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import PasswordInput from "@/components/shared/PasswordInput";

// Helper function to extract error message from API response
const getErrorMessage = (err, defaultMsg = "An error occurred") => {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map(e => {
      const fieldPath = e.loc ? e.loc.filter(l => l !== 'body').join('.') : '';
      const fieldName = fieldPath || 'Unknown field';
      const message = e.msg || e.message || 'Invalid value';
      return `${fieldName}: ${message}`;
    }).join('. ');
  } else if (typeof detail === 'string') {
    return detail;
  } else if (detail) {
    return JSON.stringify(detail);
  }
  return err?.message || defaultMsg;
};

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Always default to "tech" - user must explicitly choose
  const [role, setRole] = useState("tech");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, logout, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the return URL from location state (set by ProtectedRoute)
  // Ignore query params (used for cache busting)
  const returnUrl = location.state?.from;

  // Clear any stale token on mount to ensure fresh login
  useEffect(() => {
    // If we have a cache-bust param, we're coming from logout - ensure clean state
    if (location.search.includes("t=")) {
      localStorage.removeItem("token");
      localStorage.removeItem("userRole");
    }
  }, [location.search]);

  // If user is already logged in and there's a return URL, redirect there
  useEffect(() => {
    if (token && returnUrl) {
      navigate(returnUrl, { replace: true });
    }
  }, [token, navigate, returnUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Ensure clean state before login
      if (token) {
        logout();
      }
      const userData = await login(email, password);
      localStorage.setItem("userRole", role);

      // Role-based landing — MOT_TESTER lands DIRECTLY on Today's MOTs
      // queue (bypasses generic tech dashboard). Otherwise honour the
      // role chip the user picked on the login form.
      // iter213b — honour location.state.from first so deep-link
      // launches (e.g. /gh/open?jobsheet_no=SJ026991) return to their
      // intended destination after login. Only trust internal routes
      // to avoid open-redirect via a crafted state.
      const apiRole = String(userData?.role || "").toUpperCase();
      let destination;
      if (returnUrl && typeof returnUrl === "string"
          && returnUrl.startsWith("/") && !returnUrl.startsWith("//")) {
        destination = returnUrl;
      } else if (apiRole === "MOT_TESTER") {
        destination = "/app/tech/mot";
      } else if (role === "foh") {
        destination = "/app/foh";
      } else {
        destination = "/app/tech/jobs";
      }
      navigate(destination, { replace: true });
    } catch (err) {
      setError(getErrorMessage(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6">
      <Card className="w-full max-w-xl" data-testid="login-card">
        <CardHeader className="text-center pt-12 pb-6">
          {/* INTERNAL ONLY: Yestimate logo on login screen */}
          <div className="flex justify-center">
            <img 
              src="/yestimate-logo.jpeg" 
              alt="Yestimate" 
              className="w-auto"
              style={{ maxHeight: '270px' }}
            />
          </div>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-100 text-red-700 rounded text-base" data-testid="login-error">
                {error}
              </div>
            )}
            
            {/* Role Selector */}
            <div>
              <Label className="text-base">Portal</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <button
                  type="button"
                  className={`p-4 border rounded text-base font-medium transition-all ${
                    role === "tech" ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                  onClick={() => setRole("tech")}
                  data-testid="role-tech-btn"
                >
                  Technician
                </button>
                <button
                  type="button"
                  className={`p-4 border rounded text-base font-medium transition-all ${
                    role === "foh" ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"
                  }`}
                  onClick={() => setRole("foh")}
                  data-testid="role-foh-btn"
                >
                  Service Advisor
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="email" className="text-base">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 text-base mt-1"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-base">Password</Label>
              <div className="mt-1">
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 text-base"
                  testId="login-password-input"
                />
              </div>
            </div>
            <Button type="submit" className="w-full h-12 text-base" disabled={loading} data-testid="login-submit-btn">
              {loading ? "Logging in..." : "Login"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
